import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import axios from 'axios';
import ShopLayout from '../components/layout/ShopLayout';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  skincare: {
    name: 'Skincare',
    description: 'Cleansers, serums, moisturizers, and treatments for radiant skin',
    image: 'https://images.unsplash.com/photo-1571206508927-2ef3026ada5d?w=1200',
  },
  makeup: {
    name: 'Makeup',
    description: 'Foundations, lipsticks, eyeshadows, and more for your perfect look',
    image: 'https://images.unsplash.com/photo-1523374488371-bf3bafcf86bb?w=1200',
  },
  haircare: {
    name: 'Haircare',
    description: 'Shampoos, conditioners, treatments, and styling products',
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1200',
  },
  fragrance: {
    name: 'Fragrance',
    description: 'Perfumes, body mists, and scented products',
    image: 'https://images.unsplash.com/photo-1729101807924-3446ca9aa480?w=1200',
  },
  'bath-body': {
    name: 'Bath & Body',
    description: 'Body lotions, scrubs, bath products, and more',
    image: 'https://images.unsplash.com/photo-1729101886078-6976c47c1e74?w=1200',
  },
  tools: {
    name: 'Tools & Brushes',
    description: 'Brushes, sponges, and beauty accessories',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1200',
  },
};

const CategoryPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const [filterOpen, setFilterOpen] = useState(false);

  const categoryInfo = CATEGORY_INFO[slug] || {
    name: slug.charAt(0).toUpperCase() + slug.slice(1),
    description: '',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1200',
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/api/products`, {
          params: { category: slug }
        });
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [slug]);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      default:
        return (b.is_bestseller ? 1 : 0) - (a.is_bestseller ? 1 : 0);
    }
  });

  return (
    <ShopLayout>
      {/* Hero Banner */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden" data-testid="category-hero">
        <div className="absolute inset-0">
          <img
            src={categoryInfo.image}
            alt={categoryInfo.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 h-full flex items-center">
          <div className="text-white">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>{categoryInfo.name}</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-medium mb-4">
              {categoryInfo.name}
            </h1>
            <p className="text-white/80 text-lg max-w-xl">
              {categoryInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 md:py-16" data-testid="products-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b">
            <p className="text-sm text-neutral-500">
              {products.length} product{products.length !== 1 ? 's' : ''}
            </p>
            
            <div className="flex items-center gap-4">
              {/* Sort - Desktop */}
              <div className="hidden md:flex items-center gap-2">
                <span className="text-sm text-neutral-500">Sort by:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] h-10 rounded-none border-neutral-300" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Top Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Filter/Sort - Mobile */}
              <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="outline" className="h-10 rounded-none" data-testid="filter-btn">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    Filter & Sort
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[50vh]">
                  <SheetHeader>
                    <SheetTitle>Sort Products</SheetTitle>
                  </SheetHeader>
                  <div className="py-6 space-y-4">
                    {[
                      { value: 'featured', label: 'Featured' },
                      { value: 'newest', label: 'Newest' },
                      { value: 'price-low', label: 'Price: Low to High' },
                      { value: 'price-high', label: 'Price: High to Low' },
                      { value: 'rating', label: 'Top Rated' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setFilterOpen(false);
                        }}
                        className={`w-full text-left py-3 px-4 ${
                          sortBy === option.value ? 'bg-neutral-100 font-medium' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-neutral-500 mb-4">No products found in this category.</p>
              <Link to="/">
                <Button variant="outline" className="rounded-none">
                  Back to Home
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {sortedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </ShopLayout>
  );
};

export default CategoryPage;
